import { useEffect, useState, type ReactElement } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { AlertCircle, RotateCcw } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { Skeleton } from '@/components/ui/skeleton'
import {
  createInvoiceRequestSchema,
  type CreateInvoiceRequest,
} from '../schemas/invoicesSchema'
import type { UseBulkInvoiceQueueResult } from '../hooks/useBulkInvoiceQueue'
import { InvoiceFilePreview } from './InvoiceFilePreview'
import { InvoiceForm } from './InvoiceForm'
import { BulkQueueNav } from './BulkQueueNav'

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const

interface CreateInvoiceDialogProps {
  queue: UseBulkInvoiceQueueResult
  defaultSaleDate: string
  year: number
  monthIndex: number
  onNavigateToMonth: (year: number, monthIndex: number) => void
}

export function CreateInvoiceDialog({
  queue,
  defaultSaleDate,
  year,
  monthIndex,
  onNavigateToMonth,
}: CreateInvoiceDialogProps): ReactElement | null {
  const { items, current, isOpen, currentIndex } = queue
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false)

  const emptyDefaults: CreateInvoiceRequest = {
    invoiceNumber: '',
    saleDate: defaultSaleDate,
    netAmount: 0,
    grossAmount: 0,
    currency: 'PLN',
    counterparty: '',
  }

  const form = useForm<CreateInvoiceRequest>({
    resolver: zodResolver(createInvoiceRequestSchema),
    defaultValues: emptyDefaults,
  })

  // Re-seed the form whenever we switch to a different queue item, or its parse
  // result becomes available.
  useEffect(() => {
    if (!current) {
      form.reset(emptyDefaults)
      return
    }
    if (current.parsed) {
      form.reset({
        invoiceNumber: current.parsed.invoiceNumber ?? '',
        saleDate: current.parsed.saleDate ?? defaultSaleDate,
        netAmount: current.parsed.netAmount ?? 0,
        grossAmount: current.parsed.grossAmount ?? 0,
        vatRate: current.parsed.vatRate != null ? current.parsed.vatRate * 100 : null,
        currency: current.parsed.currency ?? 'PLN',
        counterparty: current.parsed.counterparty ?? '',
        issueDate: current.parsed.issueDate,
        paymentDueDate: current.parsed.paymentDueDate,
      })
    } else {
      form.reset(emptyDefaults)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id, current?.parsed])

  const handleSubmit = async (values: CreateInvoiceRequest): Promise<void> => {
    if (!current) return
    const payload = {
      ...values,
      vatRate: values.vatRate != null ? values.vatRate / 100 : null,
    }
    const invoice = await queue.saveCurrent(payload)
    if (!invoice) {
      const err = queue.current?.saveError ?? 'Unknown error'
      toast.error('Failed to save invoice', { description: err })
      return
    }

    const invoiceDate = new Date(invoice.saleDate)
    const invoiceMonth = invoiceDate.getMonth()
    const invoiceYear = invoiceDate.getFullYear()

    if (invoiceMonth !== monthIndex || invoiceYear !== year) {
      const monthName = MONTH_NAMES[invoiceMonth]!
      const monthLabel = `${monthName} ${invoiceYear}`
      toast.success(`Invoice saved to ${monthLabel}`, {
        action: {
          label: `Go to ${monthName}`,
          onClick: () => onNavigateToMonth(invoiceYear, invoiceMonth),
        },
      })
    } else {
      toast.success('Invoice created')
    }
  }

  const handleDialogChange = (open: boolean): void => {
    if (open) return
    if (queue.hasUnfinished) {
      setConfirmCloseOpen(true)
      return
    }
    queue.close()
  }

  const confirmDiscardAndClose = (): void => {
    setConfirmCloseOpen(false)
    queue.close()
  }

  if (!isOpen || !current) return null

  const unfinishedCount = items.filter(
    i => i.saveStatus !== 'saved' && i.saveStatus !== 'discarded',
  ).length

  const isBulk = items.length > 1
  const isParsing = current.parseStatus === 'parsing' || current.parseStatus === 'pending'
  const isSaving = current.saveStatus === 'saving'
  const parseFailed = current.parseStatus === 'failed'

  const unfinishedAhead = items.some(
    (i, idx) =>
      idx > currentIndex && i.saveStatus !== 'saved' && i.saveStatus !== 'discarded',
  )
  const saveLabel = unfinishedAhead ? 'Save → Next' : 'Save & Close'

  return (
    <>
    <Dialog open={isOpen} onOpenChange={handleDialogChange}>
      <DialogContent className={cn('sm:max-w-[90vw] lg:max-w-6xl')}>
        <DialogHeader>
          <DialogTitle>New Invoice</DialogTitle>
          <DialogDescription>
            Attaching: {current.file.name}
          </DialogDescription>
        </DialogHeader>

        {isBulk && (
          <BulkQueueNav
            items={items}
            currentIndex={currentIndex}
            onNext={queue.next}
            onPrev={queue.prev}
            onJump={queue.goTo}
          />
        )}

        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
          <div className="min-w-0 sm:w-[70%]">
            <InvoiceFilePreview file={current.file} />
          </div>

          <div className="min-w-0 sm:w-[30%] space-y-3">
            {parseFailed && (
              <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                <div className="flex-1">
                  <div className="font-medium text-destructive">
                    Couldn&apos;t read this file
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Fill in manually or discard. {current.parseError ?? ''}
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => void queue.retryParseCurrent()}
                >
                  <RotateCcw className="h-3.5 w-3.5 mr-1" />
                  Retry
                </Button>
              </div>
            )}

            {isParsing ? (
              <div className="space-y-3">
                <Skeleton className="h-9 w-full" />
                <Skeleton className="h-9 w-full" />
                <Skeleton className="h-9 w-full" />
                <Skeleton className="h-9 w-full" />
                <Skeleton className="h-9 w-full" />
                <div className="text-xs text-muted-foreground">Reading invoice…</div>
              </div>
            ) : (
              <FormProvider {...form}>
                <InvoiceForm formId="create-invoice-form" onSubmit={handleSubmit} />
              </FormProvider>
            )}
          </div>
        </div>

        <DialogFooter className="sm:justify-between">
          <Button
            variant="ghost"
            onClick={() => queue.discardCurrent()}
            disabled={isSaving}
            className="text-muted-foreground"
          >
            Discard
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => handleDialogChange(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="create-invoice-form"
              disabled={isSaving || isParsing}
            >
              {isSaving ? (
                <>
                  <Spinner /> Saving…
                </>
              ) : (
                saveLabel
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={confirmCloseOpen} onOpenChange={setConfirmCloseOpen}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Discard unsaved invoices?</DialogTitle>
          <DialogDescription>
            You have {unfinishedCount} unsaved invoice{unfinishedCount === 1 ? '' : 's'}.
            Closing now will lose what you have not saved.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => setConfirmCloseOpen(false)}
          >
            Keep editing
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={confirmDiscardAndClose}
          >
            Discard {unfinishedCount}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  )
}
